import _ from 'lodash';
import { getVideoImage } from './utils';

export const getBackdrop = (event) => {
  return new Promise((resolve) => {
    let bd;
    const greenScreen = _.get(event, 'captureConfig.greenScreens', []).find((gs) => gs.isEnabled);
    if (greenScreen) {
      bd = greenScreen.background[0];
    } else if (_.get(event, 'proposalConfig.backdrop.enabled') && _.get(event, 'proposalConfig.backdrop.id')) {
      const idParts = event.proposalConfig.backdrop.id.split('-');
      bd = `/static/backdrops/${idParts[0]}/${idParts[1]}-full.jpg`;
    } else {
      bd = '/static/backdrops/blank/0-full.jpg';
    }
    switch (bd.split('.').pop()) {
      case 'jpg':
      case 'jpeg':
      case 'png':
        resolve({
          type: 'static',
          image: bd,
          video: null,
        });
        break;
      case 'mp4':
      case 'm4v':
        getVideoImage(bd).then((image) => {
          if (!image) {
            resolve(null);
          }
          resolve({
            type: 'video',
            image,
            video: bd,
          });
        });
        break;
      default:
        resolve(null);
    }
  });
};

export const getProposalSectionData = (eventData) => {
  if (!_.get(eventData, 'captureConfig')) {
    return {};
  }
  const {
    eventType,
    captureConfig: {
      modeConfiguration: {
        photoConfiguration = {},
        boomerangConfiguration = {},
        videoConfiguration = {},
        gifConfiguration = {},
        sloMoConfiguration = {},
      } = {},
      greenScreens = [],
      magicGreenScreenEnabled,
      poseTipsConfiguration,
      photoBackgroundRemoval,
    },
    faceEffects: { masks = { selected: [] }, beautification = {} } = {},
    enabledSharingMethods,
    airdropEnabled,
    airprintEnabled,
    surveyEnabled,
    qrCodeEnabled,
    virtualBoothEnabled,
    fullGalleryEnabled,
    printTemplateEnabled,
    printTemplate,
    proposalConfig: {
      backdrop: { enabled: backdropEnabled, url: backdropUrl, id: backdropId } = {},
      sections = [],
    } = {},
    stickersEnabled,
    stickers = [],
  } = eventData;

  // Parse settings from event data
  const isGuac = eventType === 'dslr';
  const is360 = eventType === '360';
  const isVirtual = eventType === 'virtual';
  const photosEnabled = photoConfiguration.isEnabled;
  const boomerangsEnabled = boomerangConfiguration.isEnabled;
  const videosEnabled = videoConfiguration.isEnabled;
  const gifsEnabled = gifConfiguration.isEnabled;
  const isSloMoEnabled = sloMoConfiguration.isEnabled;
  const masksEnabled = masks.enabled && masks.selected.length;
  const glamEnabled = beautification.enabled || (masks.enabled && masks.selected.some((item) => item.isGlam === true));
  const greenScreensEnabled = !!greenScreens.find((gs) => gs.isEnabled) && magicGreenScreenEnabled;
  const sharingEnabled =
    enabledSharingMethods !== 'none' || airdropEnabled || surveyEnabled || qrCodeEnabled || airprintEnabled;
  const emailSharingEnabled = ['both', 'email'].includes(enabledSharingMethods);
  const smsSharingEnabled = ['both', 'sms'].includes(enabledSharingMethods);
  const poseTipsEnabled = poseTipsConfiguration && poseTipsConfiguration.enabled;
  const selectedPoseTips = poseTipsConfiguration && poseTipsConfiguration.selection;
  const stickersSectionEnabled = isVirtual && stickersEnabled && stickers && stickers.length > 0;
  const photoBackgroundRemovalSectionEnabled = isVirtual && magicGreenScreenEnabled && greenScreens?.length > 0;
  return {
    boothPreview: {
      key: 'boothPreview',
      title: isVirtual ? 'Custom Background and Start Button' : 'Custom interface and LED lights',
      enabled: !is360,
      defaults: {
        headingText: 'A Customized Experience',
        bodyText: isVirtual
          ? 'Customize background and start button of your Virtual Booth’s home page.'
          : 'Get a customized interface and LED colors to perfectly blend in with your environment.',
      },
    },
    backdrop: {
      key: 'backdrop',
      title: 'Backdrop',
      enabled: backdropEnabled && (backdropUrl || backdropId) && !is360,
      defaults: {
        headingText: 'Backdrops to Perfectly Match Your Theme',
        bodyText:
          'Our backdrops make your photo booth the centerpiece of the event while naturally blending in with your decor.',
      },
    },
    photos: {
      key: 'photos',
      title: 'Photos',
      enabled: photosEnabled && !is360,
      defaults: {
        headingText: 'Instant Photos',
        bodyText: 'Give a personalized keepsake for everyone to take home and cherish for years.',
      },
    },
    boomerangs: {
      key: 'boomerangs',
      title: 'Boomerangs',
      enabled: boomerangsEnabled && !is360,
      defaults: {
        headingText: 'Boomerangs',
        bodyText:
          'Hilarious moments that play forward and backward and forward and backward and — well, you get the idea.',
      },
    },
    videos: {
      key: 'videos',
      title: 'Videos',
      enabled: videosEnabled && !is360,
      defaults: {
        headingText: 'Videos',
        bodyText: 'Collect a variety of moments of your guests in videos up to 120 seconds long!',
      },
    },
    gifs: {
      key: 'gifs',
      title: 'GIFs',
      enabled: gifsEnabled && !is360,
      defaults: {
        headingText: 'Animated GIFs',
        bodyText: 'We take a few photos and smash them together for a fun way to capture a series of moments.',
      },
    },
    sloMo: {
      key: 'sloMo',
      title: 'Slo-Mo',
      enabled: isGuac && isSloMoEnabled && !is360 && !isVirtual,
      defaults: {
        headingText: 'Slo-Mo',
        bodyText:
          'Savor every second of the event by slowing down the action and making the moment last longer to add intrigue, excitement, or drama to your captures.',
      },
    },
    poseTips: {
      key: 'poseTips',
      title: 'Pose Tips',
      enabled: poseTipsEnabled && selectedPoseTips && selectedPoseTips.length && !is360 && !isVirtual,
      defaults: {
        headingText: 'Pose Tips',
        bodyText: 'Our fun pose tips appear before every capture to turn all of your guests into instant models.',
      },
    },
    faceEffects: {
      key: 'faceEffects',
      title: 'Face Effects',
      enabled: (masksEnabled || glamEnabled) && !is360,
      defaults: {
        headingText: masksEnabled && glamEnabled ? 'Masks and Glam' : masksEnabled ? 'Masks' : 'Glam',
        bodyText:
          masksEnabled && glamEnabled
            ? 'Face effects to add that extra touch of creativity to every capture.'
            : masksEnabled
            ? 'Cool graphics designed to place on to your guests’ faces.'
            : 'With Glam you can give your guests a glow, smoother skin, or completely change their natural appearance.',
      },
    },
    stickers: {
      key: 'stickers',
      title: 'Stickers',
      enabled: stickersSectionEnabled,
      defaults: {
        headingText: 'Stickers',
        bodyText:
          'Add stickers to the event for guests to place on top of their captures. Select one or more from the Salsa Library, or upload your own custom stickers.',
      },
    },
    photoBackgroundRemoval: {
      key: 'photoBackgroundRemoval',
      title: 'Photo Background Removal',
      enabled: photoBackgroundRemovalSectionEnabled,
      defaults: {
        headingText: 'Custom background',
        bodyText:
          'Enable two or more green screen backgrounds for photo captures to allow your virtual guest to choose between them.',
      },
    },
    greenScreens: {
      key: 'greenScreens',
      title: 'Green Screen',
      enabled: greenScreensEnabled && !is360 && !isVirtual,
      defaults: {
        headingText: 'Green Screen Backgrounds',
        bodyText: 'Place your guests anywhere with our green screen technology.',
      },
    },
    tortillaIntro: {
      key: 'tortillaIntro',
      title: 'Tortilla Intro',
      enabled: is360,
      defaults: {
        headingText: 'Capture the Hype',
        bodyText: 'A dynamic and immersive experience, capturing moments from every angle.',
      },
    },
    tortillasBeauty: {
      key: 'tortillasBeauty',
      title: 'Tortilla’s Beauty',
      enabled: is360,
      defaults: {
        headingText: 'Discover the Elegance',
        bodyText: 'Experience elegance in every spin with our sleek 360 photo booth.',
      },
    },
    threeSixtyVideos: {
      key: 'threeSixtyVideos',
      title: '360 Videos',
      enabled: is360,
      defaults: {
        headingText: 'Cinematic 360 Videos',
        bodyText: 'Embrace the spotlight with shareable cinematic souvenirs.',
      },
    },
    brandedExperience: {
      key: 'brandedExperience',
      title: 'Branded Experience',
      enabled: is360,
      defaults: {
        headingText: 'Branded Experience',
        bodyText: 'Branded experience right from the start of the event.',
      },
    },
    sharing: {
      key: 'sharing',
      title: 'Sharing',
      enabled: sharingEnabled && !isVirtual,
      defaults: {
        headingText: 'Guests Get Their Captures Immediately',
        bodyText: 'Guests can instantly get their captures so they can start sharing the fun on social media.',
      },
    },
    email: {
      key: 'email',
      title: 'Email Sharing',
      enabled: emailSharingEnabled,
      defaults: {
        headingText: 'Customized Emails',
        bodyText: 'Add a special message and call-to-action button to your emails when shared.',
      },
    },
    sms: {
      key: 'sms',
      title: 'Text Sharing',
      enabled: smsSharingEnabled && !isVirtual,
      defaults: {
        headingText: 'Customized Texts',
        bodyText: 'Craft a unique message when the booth sends guests a text message with a link to their capture.',
      },
    },
    liveGallery: {
      key: 'liveGallery',
      title: 'Live Gallery',
      enabled: fullGalleryEnabled,
      defaults: {
        headingText: 'Instant Online Album',
        bodyText:
          'Every capture is immediately uploaded to a Live Gallery where you can instantly relive the memories from your event. A custom call-to-action button also drives traffic to your location of choice.',
      },
    },
    virtualBooth: {
      key: 'virtualBooth',
      title: 'Virtual Booth',
      enabled: virtualBoothEnabled && !is360 && !isVirtual,
      defaults: {
        headingText: 'Virtual Booth for Virtual Guests',
        bodyText: 'Create engagement and connection from guests from any place on any device.',
      },
    },
    print: {
      key: 'print',
      title: 'Printing',
      enabled: isGuac && airprintEnabled && !is360 && !isVirtual,
      defaults: {
        headingText: 'Ultra-Fast Prints, Wireless or Wired',
        bodyText:
          'With wired print speeds as fast as 15 seconds and a capacity of up to 400 prints, guests will never have to wait around.',
      },
    },
    end: {
      key: 'end',
      title: 'Call to Action section',
      defaults: {
        headingText: "Let's get this party started",
        bodyText: 'Click the button below to take the first step to elevate your gathering!',
      },
    },
  };
};

export const proposalSectionList = [
  'boothPreview',
  'backdrop',
  'photos',
  'boomerangs',
  'videos',
  'gifs',
  'sloMo',
  'poseTips',
  'faceEffects',
  'stickers',
  'photoBackgroundRemoval',
  'greenScreens',
  'tortillaIntro',
  'tortillasBeauty',
  'threeSixtyVideos',
  'brandedExperience',
  'sharing',
  'email',
  'sms',
  'liveGallery',
  'virtualBooth',
  'print',
  'end',
];

export const buildProposalPages = (event) => {
  if (!_.get(event, 'captureConfig')) {
    return null;
  }
  const {
    proposalConfig: { button = {}, sections = [], customTextEnabled },
  } = event;

  const copySections = sections
    ? sections.reduce((all, s) => {
        const aggregate = all;
        if (s && s.id) {
          aggregate[s.id] = s;
        }
        return aggregate;
      }, Object.create(null))
    : {};

  const proposalSectionData = getProposalSectionData(event);

  return proposalSectionList
    .filter((k) => {
      return (!!button.enabled && k === 'end') || (proposalSectionData[k] && proposalSectionData[k].enabled);
    })
    .map((k) => ({
      id: k,
      heading:
        (!!customTextEnabled && _.get(copySections, `${k}.headingText`)) || proposalSectionData[k].defaults.headingText,
      text: (!!customTextEnabled && _.get(copySections, `${k}.bodyText`)) || proposalSectionData[k].defaults.bodyText,
    }));
};

export const backdropData = [
  {
    id: 'blank',
    name: 'Blank',
    twoSided: false,
  },
  {
    id: 'blossom',
    slug: 'atlas-blossom',
    name: 'Blossom',
    twoSided: true,
  },
  {
    id: 'breeze',
    slug: 'atlas-breeze-backdrop',
    name: 'Breeze',
    twoSided: true,
  },
  {
    id: 'bubblegum',
    slug: 'atlas-bubblegum',
    name: 'Bubblegum',
    twoSided: true,
  },
  {
    id: 'screen',
    slug: 'atlas-system-the-screen-backdrop',
    name: 'Chroma',
    twoSided: true,
  },
  {
    id: 'downtown',
    slug: 'atlas-downtown-backdrop',
    name: 'Downtown',
    twoSided: true,
  },
  {
    id: 'eames',
    slug: 'atlas-eames',
    name: 'Eames',
    twoSided: true,
  },
  {
    id: 'fruity',
    slug: 'atlas-fruity',
    name: 'Fruity',
    twoSided: true,
  },
  {
    id: 'gatsby',
    slug: 'atlas-gatsby-backdrop',
    name: 'Gatsby',
    twoSided: true,
  },
  {
    id: 'grapefruit',
    slug: 'atlas-system-the-grapefruit-backdrop',
    name: 'Grapefruit',
    twoSided: true,
  },
  {
    id: 'kaimu',
    slug: 'atlas-kaimu',
    name: 'Kaimu',
    twoSided: true,
  },
  {
    id: 'marble',
    slug: 'atlas-marble-backdrop',
    name: 'Marble',
    twoSided: true,
  },
  /* {
    id: 'moonlight',
    slug: 'atlas-system-the-moonlight-backdrop',
    name: 'Moonlight',
    twoSided: true
  }, */
  {
    id: 'natural',
    slug: 'atlas-natural-backdrop',
    name: 'Natural',
    twoSided: true,
  },
  {
    id: 'petal',
    slug: 'atlas-petal-backdrop',
    name: 'Petal',
    twoSided: true,
  },
  {
    id: 'sahara',
    slug: 'atlas-sahara',
    name: 'Sahara',
    twoSided: true,
  },
  {
    id: 'sedona',
    slug: 'atlas-sedona',
    name: 'Sedona',
    twoSided: true,
  },
  {
    id: 'spring',
    slug: 'atlas-spring-backdrop',
    name: 'Spring',
    twoSided: true,
  },
  {
    id: 'storm',
    slug: 'atlas-storm',
    name: 'Storm',
    twoSided: true,
  },
  {
    id: 'summer',
    slug: 'atlas-summer-backdrop',
    name: 'Summer',
    twoSided: true,
  },
  /* {
    id: 'sunburst',
    slug: 'atlas-sunburst',
    name: 'Sunburst',
    twoSided: true
  },
  {
    id: 'swirl',
    slug: 'atlas-swirl',
    name: 'Swirl',
    twoSided: true
  }, */
  {
    id: 'terrazzo',
    slug: 'atlas-terrazzo',
    name: 'Terrazzo',
    twoSided: true,
  },
  /* {
    id: 'watermelon',
    slug: 'atlas-system-the-watermelon-backdrop',
    name: 'watermelon',
    twoSided: true
  }, */
];
